import { useEffect, useState } from "react";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import {
  Section, Cell, Switch, Spinner, Caption, Banner, Button,
} from "@telegram-apps/telegram-ui";
import { api, openAuthedFile } from "../../lib/api.js";
import { usePageBack } from "../../lib/telegram.js";

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("ru-RU"); } catch { return iso; }
}

export default function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  usePageBack("/superadmin/users");
  const { me } = useOutletContext() || {};
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  function reload() {
    setData(null);
    api(`/api/v1/superadmin/users/${id}`).then(setData).catch((e) => setError(e.message));
  }
  useEffect(reload, [id]);

  async function toggleSuperadmin(value) {
    setBusy(true);
    try {
      await api(`/api/v1/superadmin/users/${id}/superadmin`, {
        method: "POST", body: JSON.stringify({ value }),
      });
      reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (error && !data) return <Section><Cell multiline>Ошибка: {error}</Cell></Section>;
  if (!data) return <Section><Cell><Spinner size="m" /></Cell></Section>;

  const u = data.user;
  const name =
    [u.last_name, u.first_name, u.middle_name].filter(Boolean).join(" ") || "(без имени)";
  const isSelf = Number(me?.user?.id) === Number(u.id);

  return (
    <>
      <Banner
        type="section"
        header={name}
        subheader={u.tg_username ? `@${u.tg_username}` : `tg:${u.tg_user_id}`}
        description={`Создан ${formatDate(u.created_at)}`}
      >
        <Button mode="plain" onClick={() => navigate(-1)}>← Назад</Button>
      </Banner>

      <Section header="Активность">
        <Cell
          onClick={() => navigate(`/superadmin/messages?user_id=${u.id}`)}
          after="→"
          subtitle="Все DM этого пилота и бота — в обе стороны."
        >✉️ Сообщения бота</Cell>
      </Section>

      <Section header="Telegram + контакты">
        <Cell subtitle="Telegram ID" after={String(u.tg_user_id)}>tg_user_id</Cell>
        <Cell subtitle="Внутренний ID" after={`#${u.id}`}>users.id</Cell>
        <Cell subtitle="Фамилия" after={u.last_name || "—"}>last_name</Cell>
        <Cell subtitle="Имя" after={u.first_name || "—"}>first_name</Cell>
        <Cell subtitle="Отчество" after={u.middle_name || "—"}>middle_name</Cell>
        <Cell subtitle="Класс" after={u.skill_class || "—"}>skill_class</Cell>
        <Cell subtitle="ФАСР ID" after={u.fasr_id || "—"}>fasr_id</Cell>
        <Cell subtitle="Телефон" after={u.phone || "—"}>phone</Cell>
      </Section>

      <Section header="Права">
        <Cell
          after={
            <Switch
              checked={u.is_superadmin}
              disabled={busy || isSelf}
              onChange={(e) => toggleSuperadmin(e.target.checked)}
            />
          }
          subtitle={isSelf ? "Себя нельзя разжаловать здесь" : "Полный доступ к порталу"}
        >
          🛡 Суперадмин
        </Cell>
      </Section>

      <Section header={`Аэродромы — администратор (${data.admin_assignments.length})`}>
        {data.admin_assignments.length === 0 && (
          <Cell><Caption level="1">Не является администратором ни одного аэродрома.</Caption></Cell>
        )}
        {data.admin_assignments.map((a) => (
          <Cell
            key={a.slug}
            subtitle={a.revoked_at ? `Отозвано ${formatDate(a.revoked_at)}` : `С ${formatDate(a.created_at)}`}
            onClick={() => navigate(`/superadmin/airfields/${a.slug}`)}
            after="→"
          >
            {a.revoked_at ? "🚫" : "🛠"} {a.name}
          </Cell>
        ))}
      </Section>

      <Section header={`Членство (${data.memberships.length})`}>
        {data.memberships.length === 0 && (
          <Cell><Caption level="1">Нет действующего членства.</Caption></Cell>
        )}
        {data.memberships.map((m) => {
          const v = m.verification_status || "unverified";
          const until = m.verified_until ? ` · до ${m.verified_until}` : "";
          return (
            <Cell
              key={m.slug}
              multiline
              subtitle={`${m.status} · верификация: ${v}${until}${m.verification_note ? ` · ${m.verification_note}` : ""}`}
              onClick={() => navigate(`/superadmin/airfields/${m.slug}`)}
              after="→"
            >{m.name}</Cell>
          );
        })}
      </Section>

      <Section header={`Воздушные суда (${data.aircraft.length})`}>
        {data.aircraft.length === 0 && (
          <Cell><Caption level="1">Не зарегистрировано.</Caption></Cell>
        )}
        {data.aircraft.map((ac) => (
          <Cell key={ac.id} subtitle={ac.class_code}>
            {ac.is_primary ? "★ " : ""}{ac.name}
          </Cell>
        ))}
      </Section>

      <Section header={`Брони — последние ${data.bookings.length}`}>
        {data.bookings.length === 0 && <Cell><Caption level="1">Бронирований нет.</Caption></Cell>}
        {data.bookings.map((b) => (
          <Cell
            key={b.id}
            subtitle={`${b.airfield_name} · ${b.class_code} · ${b.status}`}
          >
            {formatDate(b.start_at)} – {formatDate(b.end_at)}
          </Cell>
        ))}
      </Section>

      <Section header={`Предупреждения (${data.warnings.length})`}>
        {data.warnings.length === 0 && <Cell><Caption level="1">Без предупреждений.</Caption></Cell>}
        {data.warnings.map((w) => (
          <Cell
            key={w.id}
            multiline
            subtitle={`${w.airfield_name} · ${formatDate(w.created_at)}${w.edited_at ? " (правка)" : ""}`}
          >{w.reason}</Cell>
        ))}
      </Section>

      <Section header={`Документы (${data.documents.length})`}>
        {data.documents.length === 0 && <Cell><Caption level="1">Документов нет.</Caption></Cell>}
        {data.documents.map((d) => (
          <Cell
            key={d.id}
            subtitle={`${d.kind} · ${d.status}${d.expires_on ? ` · истекает ${d.expires_on}` : ""}`}
            onClick={() => openAuthedFile(`/api/v1/superadmin/documents/${d.id}/file`).catch((e) => setError(e.message))}
            after="↗"
          >📎 #{d.id}</Cell>
        ))}
      </Section>
    </>
  );
}
